import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Anchor, 
  Users, 
  Clock, 
  MapPin, 
  ChevronLeft, 
  ChevronRight,
  Check,
  CreditCard,
  FileText,
  Download,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { PaymentForm, CreditCard as SquareCreditCard } from 'react-square-web-payments-sdk';
import { useLanguage } from '@/i18n/LanguageContext';
import { t } from '@/i18n/translations';
import LanguageSelector from '@/components/LanguageSelector';
import { fr } from 'date-fns/locale';
import { format, addDays, isBefore, startOfToday } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo
const LOGO_HEADER = "https://customer-assets.emergentagent.com/job_yacht-reserve-pro/artifacts/t54ah8ex_logo%203%20BELLA%20BARCA.png";

const stepsConfig = [
  { id: 1, nameKey: 'booking.step1', icon: Anchor },
  { id: 2, nameKey: 'booking.step2', icon: Users },
  { id: 3, nameKey: 'booking.step3', icon: FileText },
  { id: 4, nameKey: 'booking.step4', icon: CreditCard }
];

export default function BookingPage() {
  const { lang } = useLanguage();
  const { boatId } = useParams();
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [boat, setBoat] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  // Form state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [passengers, setPassengers] = useState(2);
  
  // Customer info
  const [customerInfo, setCustomerInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: ''
  });
  
  // Booking state
  const [bookingId, setBookingId] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [contractSigned, setContractSigned] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [cgvContent, setCgvContent] = useState('');

  useEffect(() => {
    fetchBoat();
    fetchPricing();
    fetchBlockedDates();
    fetchCGV();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability();
      // Reset selections when date changes
      setSelectedTimeSlot('');
      setSelectedDuration(null);
    }
  }, [selectedDate]);

  const fetchCGV = async () => {
    try {
      const res = await fetch(`${API}/cgv`);
      if (res.ok) {
        const data = await res.json();
        setCgvContent(data.content || '');
      }
    } catch (e) {
      console.error('Failed to fetch CGV:', e);
    }
  };

  const fetchBoat = async (retries = 2) => {
    try {
      const res = await fetch(`${API}/boats`);
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      if (data.length > 0) setBoat(data[0]);
    } catch (e) {
      if (retries > 0) {
        setTimeout(() => fetchBoat(retries - 1), 1500);
      } else {
        toast.error('Erreur lors du chargement du bateau');
      }
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${API}/pricing`);
      const data = await res.json();
      setPricing(data);
    } catch (e) {
      toast.error('Erreur lors du chargement des tarifs');
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const res = await fetch(`${API}/blocked-dates`);
      const data = await res.json();
      setBlockedDates(data || []);
    } catch (e) {
      console.error('Error fetching blocked dates:', e);
    }
  };

  const fetchAvailability = async () => {
    if (!selectedDate) return;
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await fetch(`${API}/availability/${dateStr}`);
      const data = await res.json();
      setAvailability(data);
      setAvailableSlots(data.available_slots || []);
      setPricing(data.rates || []);
    } catch (e) {
      console.error('Error fetching availability:', e);
    }
  };

  const getSeason = () => {
    if (!selectedDate) return 'basse';
    const month = selectedDate.getMonth() + 1;
    return (month === 7 || month === 8) ? 'haute' : 'basse';
  };

  const getAvailableRates = () => {
    const season = getSeason();
    return pricing.filter(p => p.season === season);
  };

  const calculateTotalPrice = () => {
    if (!selectedTimeSlot) return 0;
    const duration = calculateDuration(selectedTimeSlot);
    const season = getSeason();
    // Match by time_slot first (exact or includes) for high season compound slots
    const rate = pricing.find(p => 
      p.season === season && (
        p.time_slot === selectedTimeSlot || 
        (p.time_slot && p.time_slot.includes(selectedTimeSlot))
      )
    ) || pricing.find(p => p.season === season && p.duration_hours === duration);
    return rate ? rate.price : Math.round(duration * 45 * 100);
  };

  const calculateDuration = (slot) => {
    if (!slot || !slot.includes('-')) return 0;
    const [start, end] = slot.split('-');
    const startMinutes = parseTime(start);
    const endMinutes = parseTime(end);
    return (endMinutes - startMinutes) / 60;
  };

  const parseTime = (timeStr) => {
    const clean = timeStr.replace('h', ':').replace('H', ':');
    const parts = clean.split(':');
    const hours = parseInt(parts[0]);
    const minutes = parts[1] ? parseInt(parts[1]) : 0;
    return hours * 60 + minutes;
  };

  const formatSlotDisplay = (slot) => {
    // Convert "10h00-13h00" to more readable format
    return slot.replace(/-/g, ' - ');
  };

  const handleCustomerChange = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    if (!boat) {
      toast.error('Erreur: bateau non disponible');
      return false;
    }
    if (!selectedDate) {
      toast.error('Veuillez sélectionner une date');
      return false;
    }
    if (!selectedTimeSlot) {
      toast.error('Veuillez sélectionner un créneau horaire');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const { first_name, last_name, email, phone } = customerInfo;
    if (!first_name || !last_name) {
      toast.error('Veuillez entrer votre nom complet');
      return false;
    }
    if (!email || !email.includes('@')) {
      toast.error('Veuillez entrer un email valide');
      return false;
    }
    if (!phone) {
      toast.error('Veuillez entrer votre numéro de téléphone');
      return false;
    }
    return true;
  };

  const createBooking = async () => {
    setLoading(true);
    try {
      const duration = calculateDuration(selectedTimeSlot);
      
      const res = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          boat_id: boat?.id || 'auto', // Server will auto-assign
          date: format(selectedDate, 'yyyy-MM-dd'),
          time_slot: selectedTimeSlot,
          duration_hours: duration,
          passengers: passengers,
          customer: customerInfo
        })
      });
      
      if (!res.ok) throw new Error('Erreur lors de la création de la réservation');
      
      const data = await res.json();
      setBookingId(data.booking.id);
      setBookingData(data.booking);
      toast.success('Réservation créée avec succès');
      setCurrentStep(3);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const signContract = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Veuillez signer le contrat');
      return;
    }
    // Just store signature in state - booking created only after payment
    setSignatureData(signatureRef.current.toDataURL());
    setContractSigned(true);
    toast.success('Contrat signé avec succès');
    setCurrentStep(4);
  };

  const processPayment = async (token, verificationToken) => {
    setLoading(true);
    try {
      // Step 1: Create booking NOW (only after payment intent)
      const duration = calculateDuration(selectedTimeSlot);
      const createRes = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          boat_id: boat?.id || 'auto',
          date: format(selectedDate, 'yyyy-MM-dd'),
          time_slot: selectedTimeSlot,
          duration_hours: duration,
          passengers: passengers,
          customer: customerInfo
        })
      });
      
      if (!createRes.ok) {
        const errData = await createRes.json().catch(() => ({}));
        throw new Error(errData.detail || 'Erreur lors de la création de la réservation');
      }
      
      const createData = await createRes.json();
      const newBookingId = createData.booking.id;
      setBookingId(newBookingId);
      setBookingData(createData.booking);
      
      // Step 2: Sign the contract
      await fetch(`${API}/bookings/${newBookingId}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: newBookingId,
          signature_data: signatureData
        })
      });
      
      // Step 3: Process payment
      const res = await fetch(`${API}/bookings/${newBookingId}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: newBookingId,
          source_id: token,
          customer_email: customerInfo.email,
          verification_token: verificationToken || undefined
        })
      });
      
      if (!res.ok) {
        // Payment failed - delete the booking so it doesn't block the boat
        await fetch(`${API}/bookings/${newBookingId}`, { method: 'DELETE' }).catch(() => {});
        const error = await res.json().catch(() => ({}));
        throw new Error(error.detail || 'Erreur de paiement');
      }
      
      setPaymentComplete(true);
      toast.success('Paiement effectué avec succès !');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (type) => {
    try {
      const endpoint = type === 'contract' 
        ? `${API}/bookings/${bookingId}/contract/pdf`
        : `${API}/bookings/${bookingId}/invoice/pdf`;
      
      const res = await fetch(endpoint);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_${bookingId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) {
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Memoize disabledDays to ensure Calendar re-renders when blockedDates changes
  const disabledDays = useCallback((date) => {
    if (isBefore(date, startOfToday())) return true;
    // Check if date is in a blocked range
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDates.some(b => dateStr >= b.start_date && dateStr <= b.end_date);
  }, [blockedDates]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#dec5ae]/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img src={LOGO_HEADER} alt="A Bella Barca" className="h-10" />
            </Link>
            <Link to="/" className="text-sm text-[#858275] hover:text-[#0a57a4] flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              {t('booking.backHome', lang)}
            </Link>
            <LanguageSelector />
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Progress Steps */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center justify-between">
            {stepsConfig.map((step, i) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 transition-colors ${
                  currentStep >= step.id 
                    ? 'bg-[#0a57a4] border-[#0a57a4] text-white' 
                    : 'border-[#dec5ae] text-[#858275]'
                }`}>
                  {currentStep > step.id ? (
                    <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <step.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </div>
                <span className={`ml-2 sm:ml-3 text-xs sm:text-sm hidden sm:block ${
                  currentStep >= step.id ? 'text-[#0a57a4] font-medium' : 'text-[#858275]'
                }`}>
                  {t(step.nameKey, lang)}
                </span>
                {i < stepsConfig.length - 1 && (
                  <div className={`w-6 sm:w-12 md:w-24 h-0.5 mx-2 sm:mx-4 ${
                    currentStep > step.id ? 'bg-[#0a57a4]' : 'bg-[#dec5ae]'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Date & Time Slot Selection */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="grid lg:grid-cols-2 gap-8"
            >
              {/* Left Column - Selection */}
              <div className="space-y-8">
                {/* Boat Display (single boat) */}
                {boat && (
                  <div className="bg-white p-6 rounded-sm border border-[#dec5ae]/30">
                    <h3 className="font-heading text-xl mb-4 text-[#0a57a4]">{t('booking.yourBoat', lang)}</h3>
                    <div className="flex items-center gap-4">
                      <img 
                        src={boat.images?.[0]} 
                        alt={boat.name}
                        className="w-24 h-20 object-cover rounded-sm"
                      />
                      <div>
                        <h4 className="font-semibold text-[#0a57a4]">{boat.name}</h4>
                        <p className="text-sm text-[#858275]">{boat.model}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-[#858275]">
                          <span>{boat.capacity} pers.</span>
                          <span>{boat.power}</span>
                          <span>{boat.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Date Selection */}
                <div className="bg-white p-6 rounded-sm border border-[#dec5ae]/30">
                  <h3 className="font-heading text-xl mb-4 text-[#0a57a4]">{t('booking.selectDate', lang)}</h3>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disabledDays}
                    locale={fr}
                    className="w-full"
                    classNames={{
                      months: "w-full",
                      month: "w-full space-y-4",
                      table: "w-full border-collapse",
                      head_row: "flex w-full",
                      head_cell: "text-[#858275] rounded-md flex-1 font-normal text-sm",
                      row: "flex w-full mt-2",
                      cell: "flex-1 relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#dec5ae]/30 [&:has([aria-selected])]:rounded-md",
                      day: "h-10 w-full p-0 font-normal rounded-md hover:bg-[#099dbb]/10 aria-selected:opacity-100 transition-colors",
                      day_selected: "bg-[#0a57a4] text-white hover:bg-[#0a57a4] hover:text-white focus:bg-[#0a57a4] focus:text-white",
                      day_today: "bg-[#dec5ae]/40 text-[#0a57a4] font-semibold",
                      day_outside: "text-[#858275]/40 aria-selected:bg-[#dec5ae]/20 aria-selected:text-[#858275]",
                      day_disabled: "text-[#858275]/30",
                      caption: "flex justify-center pt-1 relative items-center mb-2",
                      caption_label: "text-base font-semibold text-[#0a57a4]",
                      nav: "space-x-1 flex items-center",
                      nav_button: "h-8 w-8 bg-transparent p-0 opacity-60 hover:opacity-100 border border-[#dec5ae]/30 rounded-md inline-flex items-center justify-center",
                      nav_button_previous: "absolute left-1",
                      nav_button_next: "absolute right-1",
                    }}
                    data-testid="booking-calendar"
                  />
                </div>

                {/* Time Slot Selection */}
                {selectedDate && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white p-6 rounded-sm border border-[#dec5ae]/30"
                  >
                    <h3 className="font-heading text-xl mb-4 text-[#0a57a4]">
                      Créneaux disponibles - {getSeason() === 'haute' ? 'Haute Saison' : 'Basse Saison'}
                    </h3>
                    
                    {availableSlots.length === 0 ? (
                      <div className="text-center py-8 text-[#858275]">
                        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-[#099dbb]" />
                        <p>Chargement des disponibilités...</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Haute saison: fixed slots */}
                        {getSeason() === 'haute' && (
                          <div className="grid gap-3">
                            {availableSlots.map((slotInfo, i) => {
                              const duration = calculateDuration(slotInfo.slot);
                              const season = getSeason();
                              const rate = pricing.find(p => p.time_slot === slotInfo.slot || (p.time_slot && p.time_slot.includes(slotInfo.slot)))
                                || pricing.find(p => p.season === season && p.duration_hours === duration);
                              return (
                                <button
                                  key={i}
                                  onClick={() => setSelectedTimeSlot(slotInfo.slot)}
                                  className={`p-4 border rounded-sm text-left transition-all ${
                                    selectedTimeSlot === slotInfo.slot
                                      ? 'border-[#0a57a4] bg-[#0a57a4]/5'
                                      : 'border-[#dec5ae]/30 hover:border-[#099dbb]'
                                  }`}
                                  data-testid={`slot-${i}`}
                                >
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="font-semibold text-[#0a57a4]">{formatSlotDisplay(slotInfo.slot)}</p>
                                      <p className="text-sm text-[#858275]">{duration >= 6 ? 'Journée' : duration >= 3 ? '1/2 journée' : `${duration}h de navigation`}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-heading text-2xl text-[#0a57a4]">{rate ? rate.price / 100 : Math.round(duration * 45)}€ <span className="text-xs font-normal">TTC</span></p>
                                      <p className="text-xs text-[#099dbb]">{slotInfo.boats_available} bateau{slotInfo.boats_available > 1 ? 'x' : ''} dispo</p>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Basse saison: select duration then start time */}
                        {getSeason() === 'basse' && (
                          <>
                            <div>
                              <Label className="text-sm font-medium mb-2 block">Durée de location</Label>
                              <Select 
                                value={selectedDuration?.toString() || ''} 
                                onValueChange={(v) => {
                                  setSelectedDuration(parseFloat(v));
                                  setSelectedTimeSlot('');
                                }}
                              >
                                <SelectTrigger data-testid="duration-select">
                                  <SelectValue placeholder="Sélectionnez une durée" />
                                </SelectTrigger>
                                <SelectContent>
                                  {getAvailableRates().sort((a, b) => a.duration_hours - b.duration_hours).map(rate => (
                                    <SelectItem key={rate.id} value={rate.duration_hours.toString()}>
                                      {rate.duration_label} - {rate.price / 100}€ TTC
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {selectedDuration && (
                              <div>
                                <Label className="text-sm font-medium mb-2 block">Heure de départ</Label>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                  {availableSlots
                                    .filter(s => {
                                      const duration = calculateDuration(s.slot);
                                      return Math.abs(duration - selectedDuration) < 0.1;
                                    })
                                    .map((slotInfo, i) => {
                                      const startTime = slotInfo.slot.split('-')[0];
                                      return (
                                        <button
                                          key={i}
                                          onClick={() => setSelectedTimeSlot(slotInfo.slot)}
                                          className={`p-3 border rounded-sm text-center transition-all ${
                                            selectedTimeSlot === slotInfo.slot
                                              ? 'border-[#0a57a4] bg-[#0a57a4] text-white'
                                              : 'border-[#dec5ae]/30 hover:border-[#099dbb]'
                                          }`}
                                          data-testid={`time-${i}`}
                                        >
                                          <p className="font-semibold">{startTime}</p>
                                          <p className="text-xs opacity-70">{slotInfo.boats_available} dispo</p>
                                        </button>
                                      );
                                    })}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        <div>
                          <Label className="text-sm font-medium mb-2 block">{t('booking.passengers', lang)}</Label>
                          <Select value={passengers.toString()} onValueChange={(v) => setPassengers(parseInt(v))}>
                            <SelectTrigger data-testid="passengers-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5].map(n => (
                                <SelectItem key={n} value={n.toString()}>{n} personne{n > 1 ? 's' : ''}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              {/* Right Column - Summary */}
              <div className="lg:sticky lg:top-24 h-fit">
                <div className="bg-white p-6 rounded-sm border border-[#dec5ae]/30">
                  <h3 className="font-heading text-xl mb-6 text-[#0a57a4]">{t('booking.summary', lang)}</h3>
                  
                  {boat && (
                    <div className="mb-6">
                      <img 
                        src={boat.images?.[0]} 
                        alt={boat.name}
                        className="w-full h-48 object-cover rounded-sm mb-4"
                      />
                      <h4 className="font-semibold text-lg text-[#0a57a4]">{boat.name}</h4>
                      <p className="text-sm text-[#858275]">{boat.model}</p>
                    </div>
                  )}

                  <div className="space-y-3 py-4 border-t border-[#dec5ae]/30">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#858275]">Date</span>
                      <span className="font-medium">
                        {selectedDate ? format(selectedDate, 'dd MMMM yyyy', { locale: fr }) : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#858275]">Créneau</span>
                      <span className="font-medium">{selectedTimeSlot ? formatSlotDisplay(selectedTimeSlot) : '-'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#858275]">Durée</span>
                      <span className="font-medium">{selectedTimeSlot ? `${calculateDuration(selectedTimeSlot)}h` : '-'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#858275]">Passagers</span>
                      <span className="font-medium">{passengers}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#858275]">Saison</span>
                      <span className="font-medium capitalize">{getSeason()}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-[#dec5ae]/30">
                    <div className="flex justify-between items-center">
                      <span className="text-[#858275]">Total TTC</span>
                      <span className="font-heading text-3xl text-[#0a57a4]">
                        {(calculateTotalPrice() / 100).toFixed(0)}€
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-[#858275] mt-1">
                      <span>HT : {(calculateTotalPrice() / 100 / 1.20).toFixed(2)}€</span>
                      <span>TVA 20% : {(calculateTotalPrice() / 100 - calculateTotalPrice() / 100 / 1.20).toFixed(2)}€</span>
                    </div>
                    <p className="text-xs text-[#858275] mt-1">Carburant inclus</p>
                  </div>

                  <Button 
                    onClick={nextStep}
                    className="w-full mt-6 bg-[#0a57a4] hover:bg-[#099dbb] text-white py-6"
                    data-testid="step1-next-btn"
                    disabled={!selectedTimeSlot}
                  >
                    Continuer
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Customer Information */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="grid lg:grid-cols-2 gap-8"
            >
              <div className="bg-white p-6 rounded-sm border border-[#dec5ae]/30">
                <h3 className="font-heading text-xl mb-6 text-[#0a57a4]">{t('booking.yourInfo', lang)}</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">Prénom *</Label>
                      <Input
                        id="first_name"
                        value={customerInfo.first_name}
                        onChange={(e) => handleCustomerChange('first_name', e.target.value)}
                        placeholder="Jean"
                        data-testid="input-firstname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Nom *</Label>
                      <Input
                        id="last_name"
                        value={customerInfo.last_name}
                        onChange={(e) => handleCustomerChange('last_name', e.target.value)}
                        placeholder="Dupont"
                        data-testid="input-lastname"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => handleCustomerChange('email', e.target.value)}
                      placeholder="jean.dupont@email.com"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) => handleCustomerChange('phone', e.target.value)}
                      placeholder="+33 6 00 00 00 00"
                      data-testid="input-phone"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Adresse</Label>
                    <Input
                      id="address"
                      value={customerInfo.address}
                      onChange={(e) => handleCustomerChange('address', e.target.value)}
                      placeholder="123 rue de la Mer"
                      data-testid="input-address"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postal_code">Code postal</Label>
                      <Input
                        id="postal_code"
                        value={customerInfo.postal_code}
                        onChange={(e) => handleCustomerChange('postal_code', e.target.value)}
                        placeholder="75001"
                        data-testid="input-postal"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">Ville</Label>
                      <Input
                        id="city"
                        value={customerInfo.city}
                        onChange={(e) => handleCustomerChange('city', e.target.value)}
                        placeholder="Paris"
                        data-testid="input-city"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <Button 
                    variant="outline" 
                    onClick={prevStep}
                    className="flex-1 order-2 sm:order-1"
                    data-testid="step2-back-btn"
                  >
                    <ChevronLeft className="mr-2 w-4 h-4" />
                    {t('booking.back', lang)}
                  </Button>
                  <Button 
                    onClick={nextStep}
                    disabled={loading}
                    className="flex-1 bg-[#0a57a4] hover:bg-[#099dbb] order-1 sm:order-2"
                    data-testid="step2-next-btn"
                  >
                    {loading ? t('booking.creating', lang) : t('booking.continue', lang)}
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Summary */}
              <div className="lg:sticky lg:top-24 h-fit">
                <div className="bg-white p-6 rounded-sm border border-[#dec5ae]/30">
                  <h3 className="font-heading text-xl mb-6 text-[#0a57a4]">Votre réservation</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#858275]">Bateau</span>
                      <span className="font-medium">{boat?.name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A5568]">Date</span>
                      <span className="font-medium">
                        {selectedDate ? format(selectedDate, 'dd/MM/yyyy') : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A5568]">Créneau</span>
                      <span className="font-medium">{selectedTimeSlot}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A5568]">Durée</span>
                      <span className="font-medium">{selectedDuration}h</span>
                    </div>
                  </div>

                  <div className="pt-4 mt-4 border-t border-[#dec5ae]/30">
                    <div className="flex justify-between items-center">
                      <span className="text-[#858275]">Total TTC</span>
                      <span className="font-heading text-3xl text-[#0a57a4]">
                        {(calculateTotalPrice() / 100).toFixed(0)}€
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-[#858275] mt-1">
                      <span>HT : {(calculateTotalPrice() / 100 / 1.20).toFixed(2)}€</span>
                      <span>TVA 20% : {(calculateTotalPrice() / 100 - calculateTotalPrice() / 100 / 1.20).toFixed(2)}€</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Contract */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-4xl mx-auto"
            >
              <div className="bg-white p-4 sm:p-8 rounded-sm border border-[#dec5ae]/30">
                <h3 className="font-heading text-xl sm:text-2xl mb-6 text-center text-[#0a57a4]">
                  {t('booking.contractTitle', lang)}
                </h3>
                
                {/* Contract content */}
                <div className="prose prose-sm max-w-none mb-8 p-3 sm:p-6 bg-[#dec5ae]/10 rounded-sm text-sm max-h-[500px] overflow-y-auto">
                  <p className="text-right text-[#858275] mb-4">N° {bookingId ? bookingId.slice(0, 8).toUpperCase() : '---'}</p>
                  
                  {/* LOUEUR */}
                  <div className="mb-4 pb-4 border-b border-[#dec5ae]/30">
                    <p className="font-bold text-[#0a57a4]">LOUEUR :</p>
                    <p>Nom / Raison sociale : <strong>A BELLA BARCA</strong></p>
                    <p>N° SIRET : <strong>940 608 664</strong></p>
                    <p>Adresse : <strong>CHEMIN NOTRE-DAME DE LA SERRA 20260 CALVI</strong></p>
                    <p>Téléphone : <strong>0687999639</strong></p>
                    <p>Email : <strong>contact@abellabarca.com</strong></p>
                  </div>

                  {/* LOCATAIRE */}
                  <div className="mb-6 pb-4 border-b border-[#dec5ae]/30">
                    <p className="font-bold text-[#0a57a4]">LOCATAIRE :</p>
                    <p>Nom / Prénom : <strong>{customerInfo.first_name} {customerInfo.last_name}</strong></p>
                    <p>Adresse : <strong>{customerInfo.address || '-'} {customerInfo.postal_code || ''} {customerInfo.city || ''}</strong></p>
                    <p>Téléphone : <strong>{customerInfo.phone}</strong></p>
                    <p>Email : <strong>{customerInfo.email}</strong></p>
                  </div>

                  {/* Article 1 */}
                  <div className="mb-4">
                    <p className="font-bold text-[#0a57a4]">Article 1 - Objet du Contrat</p>
                    <p className="text-[#858275]">
                      Le présent contrat a pour objet la location d'un bateau sans permis (<strong>ROTO 450 S</strong>) appartenant au Loueur, pour une durée de location de <strong>{selectedTimeSlot ? calculateDuration(selectedTimeSlot) : '-'} heures</strong>.
                    </p>
                  </div>

                  {/* Article 2 */}
                  <div className="mb-4">
                    <p className="font-bold text-[#0a57a4]">Article 2 - Description du Bateau</p>
                    <p className="text-[#858275]">Type : <strong>Roto 450</strong></p>
                    <p className="text-[#858275]">Équipements fournis : <strong>Armement basique 2MN (5PAX), 12 Litres d'essence, 1 mouillage de 5kg</strong></p>
                  </div>

                  {/* Article 3 */}
                  <div className="mb-4">
                    <p className="font-bold text-[#0a57a4]">Article 3 - Durée de la Location</p>
                    <p className="text-[#858275]">
                      La location est consentie pour une durée de <strong>{selectedTimeSlot ? calculateDuration(selectedTimeSlot) : '-'} heures</strong>, soit de <strong>{selectedTimeSlot}</strong> le <strong>{selectedDate ? format(selectedDate, 'dd/MM/yyyy') : '-'}</strong>.
                    </p>
                    <p className="text-[#858275]">
                      Toute prolongation devra faire l'objet d'un accord écrit entre les parties et pourra être facturée en supplément.
                    </p>
                  </div>

                  {/* Article 4 */}
                  <div className="mb-4">
                    <p className="font-bold text-[#0a57a4]">Article 4 - Prix de la Location et Modalités de Paiement</p>
                    <p className="text-[#858275]">
                      Le montant de la location est fixé à <strong>{(calculateTotalPrice() / 100).toFixed(0)} €</strong>.
                    </p>
                    <p className="text-[#858275]">
                      Le paiement sera effectué par le Locataire avant la remise du Bateau, par espèces ou carte bancaire.
                    </p>
                    <p className="text-[#858275]">
                      Les prix convenus dans le contrat de location sont fermes et définitifs, ceux-ci comprennent la location du bateau avec son armement complet et une nourrice d'essence de 12 Litres. Tout carburant supplémentaire sera facturé.
                    </p>
                    <p className="text-[#858275]">
                      <strong>Le Locataire s'engage à restituer le Bateau en bon état</strong> à la fin de la location. Le Locataire restera tenu de régler les éventuels dommages constatés, sur présentation des justificatifs correspondant par le Loueur.
                    </p>
                  </div>

                  {/* CGV dynamiques depuis l'admin */}
                  {cgvContent && (
                    <div className="mt-6 pt-4 border-t border-[#dec5ae]/30">
                      <pre className="text-sm text-[#858275] whitespace-pre-wrap font-sans leading-relaxed">{cgvContent}</pre>
                    </div>
                  )}

                  <p className="mt-6 text-[#0a57a4] font-semibold">Fait à LUMIO, le {new Date().toLocaleDateString('fr-FR')}</p>
                </div>

                {/* Signature */}
                <div className="mb-6">
                  <Label className="mb-2 block text-[#0a57a4]">{t('booking.signatureLabel', lang)}</Label>
                  <div className="signature-container bg-white border-2 border-dashed border-[#dec5ae] rounded-sm overflow-hidden">
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{
                        className: 'w-full h-[200px]',
                        'data-testid': 'signature-canvas'
                      }}
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => signatureRef.current?.clear()}
                    className="mt-2 text-sm"
                  >
                    Effacer la signature
                  </Button>
                </div>

                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-sm mb-6">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    En signant ce contrat, vous acceptez les conditions générales de location 
                    et confirmez avoir pris connaissance des règles de sécurité et de navigation.
                  </p>
                </div>

                <div className="flex gap-4">
                  <Button 
                    variant="outline" 
                    onClick={prevStep}
                    className="flex-1"
                    disabled={loading}
                    data-testid="step3-back-btn"
                  >
                    <ChevronLeft className="mr-2 w-4 h-4" />
                    Retour
                  </Button>
                  <Button 
                    onClick={signContract}
                    disabled={loading}
                    className="flex-1 bg-[#0a57a4] hover:bg-[#099dbb]"
                    data-testid="sign-contract-btn"
                  >
                    {loading ? 'Signature...' : 'Signer et continuer'}
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Payment */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-2xl mx-auto"
            >
              {!paymentComplete ? (
                <div className="bg-white p-5 sm:p-8 rounded-sm border border-[#dec5ae]/30">
                  <h3 className="font-heading text-xl sm:text-2xl mb-6 text-center text-[#0a57a4]">{t('booking.securePayment', lang)}</h3>
                  
                  <div className="mb-8 p-4 sm:p-6 bg-[#dec5ae]/10 rounded-sm">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[#858275]">Montant TTC</span>
                      <span className="font-heading text-3xl text-[#0a57a4]">
                        {bookingData ? (bookingData.total_price / 100).toFixed(0) : (calculateTotalPrice() / 100).toFixed(0)}€
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-[#858275] mb-3">
                      <span>HT : {((bookingData ? bookingData.total_price : calculateTotalPrice()) / 100 / 1.20).toFixed(2)}€</span>
                      <span>TVA 20% : {((bookingData ? bookingData.total_price : calculateTotalPrice()) / 100 - (bookingData ? bookingData.total_price : calculateTotalPrice()) / 100 / 1.20).toFixed(2)}€</span>
                    </div>
                    <p className="text-sm text-[#858275]">
                      {boat?.name} - {selectedDate ? format(selectedDate, 'dd/MM/yyyy') : ''} - {selectedTimeSlot}
                    </p>
                  </div>

                  {/* Square Web Payments SDK */}
                  <div className="mb-6" data-testid="square-payment-form">
                    <PaymentForm
                      applicationId={process.env.REACT_APP_SQUARE_APPLICATION_ID}
                      locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
                      cardTokenizeResponseReceived={async (token, buyer) => {
                        if (token.status === 'OK') {
                          const verificationToken = buyer?.token || null;
                          await processPayment(token.token, verificationToken);
                        } else {
                          toast.error('Erreur de validation de la carte. Veuillez réessayer.');
                        }
                      }}
                      createVerificationDetails={() => ({
                        amount: String(((bookingData ? bookingData.total_price : calculateTotalPrice()) / 100).toFixed(2)),
                        currencyCode: 'EUR',
                        intent: 'CHARGE',
                        billingContact: {
                          givenName: customerInfo.first_name,
                          familyName: customerInfo.last_name,
                          email: customerInfo.email,
                          phone: customerInfo.phone,
                          countryCode: 'FR',
                          city: customerInfo.city || undefined,
                          postalCode: customerInfo.postal_code || undefined,
                          addressLines: customerInfo.address ? [customerInfo.address] : undefined,
                        },
                      })}
                      createPaymentRequest={() => ({
                        countryCode: 'FR',
                        currencyCode: 'EUR',
                      })}
                    >
                      <SquareCreditCard
                        buttonProps={{
                          css: {
                            backgroundColor: '#099dbb',
                            fontSize: '16px',
                            color: '#fff',
                            '&:hover': {
                              backgroundColor: '#0a57a4',
                            },
                          },
                        }}
                        style={{
                          '.input-container': {
                            borderColor: '#dec5ae',
                            borderRadius: '4px',
                          },
                          '.input-container.is-focus': {
                            borderColor: '#0a57a4',
                          },
                          input: {
                            fontSize: '14px',
                            color: '#1a1a2e',
                          },
                          'input::placeholder': {
                            color: '#858275',
                          },
                        }}
                      >
                        {loading ? 'Traitement en cours...' : `Payer ${bookingData ? (bookingData.total_price / 100).toFixed(0) : (calculateTotalPrice() / 100).toFixed(0)}€ TTC`}
                      </SquareCreditCard>
                    </PaymentForm>
                  </div>

                  <div className="flex items-center justify-center gap-2 mt-4 text-sm text-[#858275]">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    {t('booking.securedBySquare', lang)}
                  </div>

                  <div className="flex items-center justify-center gap-3 mt-3">
                    {/* CB */}
                    <div className="bg-gray-100 rounded px-2 py-1 flex items-center justify-center h-7">
                      <span className="text-[#0a57a4] font-bold text-xs tracking-tight">CB</span>
                    </div>
                    {/* Visa */}
                    <div className="bg-gray-100 rounded px-2 py-1 flex items-center justify-center h-7">
                      <svg className="h-4" viewBox="0 0 48 16" fill="none"><path d="M17.4 1.2L11.2 14.8H7.4L4.4 4.2C4.2 3.4 4 3.2 3.4 2.8C2.4 2.2 0.8 1.8 0 1.4L0.2 1.2H6.2C7 1.2 7.6 1.8 7.8 2.6L9.2 10L12.8 1.2H17.4ZM33.2 10.2C33.2 6.6 28.2 6.4 28.2 4.8C28.2 4.2 28.8 3.8 29.8 3.6C30.8 3.4 32.6 3.6 34 4.2L34.6 1.6C33.8 1.4 32.6 1 31.2 1C27 1 24 3.2 24 6.2C24 8.4 26 9.6 27.4 10.4C28.8 11.2 29.4 11.6 29.4 12.2C29.4 13.2 28.2 13.6 27 13.6C25.4 13.6 23.6 13 22.6 12.4L22 15C23.2 15.6 25 15.8 26.8 15.8C31.2 15.8 33.2 13.6 33.2 10.2ZM42.8 14.8H46.4L43.2 1.2H40.2C39.4 1.2 38.8 1.6 38.6 2.4L32.4 14.8H36.2L37 12.4H41.6L42.8 14.8ZM38 9.6L40 4.4L41 9.6H38ZM23 1.2L20 14.8H16.4L19.4 1.2H23Z" fill="#1A1F71"/></svg>
                    </div>
                    {/* Mastercard */}
                    <div className="bg-gray-100 rounded px-2 py-1 flex items-center justify-center h-7">
                      <svg className="h-4" viewBox="0 0 32 20" fill="none"><circle cx="12" cy="10" r="8" fill="#EB001B"/><circle cx="20" cy="10" r="8" fill="#F79E1B"/><path d="M16 3.6C17.8 5 19 7.4 19 10C19 12.6 17.8 15 16 16.4C14.2 15 13 12.6 13 10C13 7.4 14.2 5 16 3.6Z" fill="#FF5F00"/></svg>
                    </div>
                    {/* Amex */}
                    <div className="bg-gray-100 rounded px-2 py-1 flex items-center justify-center h-7">
                      <span className="text-[#006FCF] font-bold text-[10px] tracking-tight leading-none">AMEX</span>
                    </div>
                  </div>
                </div>
              ) : (
                /* Payment Success */
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white p-5 sm:p-8 rounded-sm border border-[#dec5ae]/30 text-center"
                >
                  <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  
                  <h3 className="font-heading text-2xl mb-2 text-[#0a57a4]">{t('booking.successTitle', lang)}</h3>
                  <p className="text-[#858275] mb-8">
                    Merci pour votre réservation. Un email de confirmation vous a été envoyé.
                  </p>

                  <div className="p-4 sm:p-6 bg-[#dec5ae]/10 rounded-sm mb-8">
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 text-left">
                      <div>
                        <p className="text-sm text-[#858275]">Référence</p>
                        <p className="font-semibold">{bookingId?.slice(0, 8).toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#4A5568]">Bateau</p>
                        <p className="font-semibold">{boat?.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#4A5568]">Date</p>
                        <p className="font-semibold">{selectedDate ? format(selectedDate, 'dd/MM/yyyy') : '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#4A5568]">Horaire</p>
                        <p className="font-semibold">{selectedTimeSlot}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
                    <Button 
                      variant="outline" 
                      onClick={() => downloadPDF('contract')}
                      className="flex-1"
                      data-testid="download-contract-btn"
                    >
                      <Download className="mr-2 w-4 h-4" />
                      {t('booking.contractPDF', lang)}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => downloadPDF('invoice')}
                      className="flex-1"
                      data-testid="download-invoice-btn"
                    >
                      <Download className="mr-2 w-4 h-4" />
                      {t('booking.invoicePDF', lang)}
                    </Button>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-sm mb-6">
                    <p className="text-sm text-blue-800">
                      <strong>Rendez-vous :</strong> Port de Sant'Ambroggio, 20260 Lumio
                      <br />
                      Présentez-vous 15 minutes avant l'heure de départ.
                    </p>
                  </div>

                  <Link to="/">
                    <Button className="bg-[#0a57a4] hover:bg-[#099dbb] text-white">
                      {t('booking.backToHome', lang)}
                    </Button>
                  </Link>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
